<template>
	<ul :name="transitionName" class="element element--list">
		<li
			v-for="(entry, index) in entries"
			:key="entry.key || index"
			@click.stop="onClick(entry)"
			:data-url="entry.url || undefined"
			:class="createClassString(entry)"
			:config-active="entry.active"
			:meaning="entry.meaning"
			:mounted="entry.mounted ? entry.mounted() : undefined"
			v-bind="entry.data"
			@animationend="onAnimationEnd(index)"
			:tabindex="entry.click || entry.url ? '0' : undefined"
			:role="entry.role ? entry.role : 'menuitem'"
			:aria-label="createAriaLabel(entry)"
			:aria-pressed="ariaPressed"
			:aria-hidden="!(entry.url || entry.click)"
			:data-cy="entry.name"
		>
			<div
				v-if="entry.cover"
				class="element__listentry__cover"
				:style="`${
					entry.cover ? ` background-image: url(${createImageURL(getLanguageValue(entry.cover))});` : ''
				}`"
			></div>
			<ElementSubIcon :name="entry.iconName || entry.meaning || entry.name" aria-hidden="true" />
			<ElementDateTime :date="entry.date" :dayname="true" :day="true" :time="true" aria-hidden="true" />
			<div class="element__listentry__text">
				<ElementSubInput
					v-if="entry.bind"
					:name="entry.name"
					type="text"
					:storeURI="entry.bind"
					:placeholder="entry.placeholder"
					:parentName="`element__listentry--${entry.name}`"
				/>
				<div v-if="entry.title" :id="entry.key" class="element__listentry__label" aria-hidden="true">
					<span v-html="getLanguageValue(entry.title)" />
				</div>
				<div
					v-if="entry.description"
					class="element__listentry__description"
					v-html="getLanguageValue(entry.description)"
					aria-hidden="true"
				></div>
				<ElementDownloadButton v-if="entry.showDownloadUI" :subPartId="entry.id" />
			</div>
			<!--  Controls for offline downloading -->
			<ElementDownloadProgress v-if="entry.showDownloadUI" :subPartId="entry.id" />
		</li>
	</ul>
</template>

<script>
/**/
import dayjs from 'dayjs';
import { mapActions } from 'vuex';

import { actionTypes } from '@/types';
import { getWeekDayName, getMonthName, toClassSave } from '@/tools/helpers';
import { getLangValue } from '@/tools/languageValue';

export default {
	/**
	 * entiries is array of objects
	 * {
	 *   click        method called when list element is clicked
	 *   id           id of list element will be used to generate class name
	 *   name         name of element will be used to generate class name
	 *   meaning      will be printed into dom attribute meaning
	 *   mounted      method will be called when list element is mounted
	 *   data         if there this object value will be rendered into the dom
	 *   date         if there date fields will be rendered
	 *   cover        background image url will be rendered as style attribute
	 *   bind         if there an input field will be rendered and bind to this object
	 *   placeholder  will be rendered as placehodler of the input field
	 *   description  will rendered as text
	 *  }
	 */
	props: {
		entries: Array,
		transitionName: {
			type: String,
			default: 'list',
		},
	},
	data() {
		return {
			ariaPressed: false,
		};
	},
	methods: {
		createAriaLabel(entry) {
			return `${
				entry.date
					? `${dayjs(entry.date).format('dddd')} ${dayjs(entry.date).format('DD.MM.YYYY')} ${dayjs(
							entry.date
					  ).format('HH.mm')}`
					: ''
			} ${entry.title ? this.getLanguageValue(entry.title) : ''} ${
				entry.description ? this.getLanguageValue(entry.description) : ''
			}`;
		},
		createImageURL(url) {
			if (!url) return;
			const size = this.$store.state.imageSize;
			return url.replace(/\/[0-9]+$/, '/' + size);
		},
		...mapActions([actionTypes.openUrl]),
		onClick(entry) {
			this.ariaPressed = true;
			if (entry.url) this.openUrl({ url: entry.url });
			if (entry.click) entry.click();
		},
		getLanguageValue(val) {
			return getLangValue(val);
		},
		onAnimationEnd(index) {
			this.$emit('onListElementAnimationEnd', index);
		},
		nameToClasses(name, baseClass) {
			// Transform name to bem style classes for every -- depth one class
			if (typeof name !== 'string') return '';
			return name.split
				? name.split('--').reduce(
						(acc, name) => {
							acc.nameParts.push(toClassSave(name));
							acc.classes = `${acc.classes} ${baseClass}--${acc.nameParts.join('--')}`;
							return acc;
						},
						{ classes: '', nameParts: [] }
				  ).classes
				: toClassSave(name);
		},
		createClassString(entry) {
			return `element__listentry ${entry.class ? this.nameToClasses(entry.class, 'element__listentry') : ''}${
				entry.id ? this.nameToClasses(entry.id, 'element__listentry') : ''
			}${entry.name ? this.nameToClasses(entry.name, 'element__listentry') : ''}
     ${entry.dataUpdated ? 'data-updated' : ''}`;
		},
	},
	created() {
		this.getWeekDayName = getWeekDayName;
		this.getMonthName = getMonthName;
	},
};
</script>

<style></style>
